import api from './api';
import { SupervisionFrequencyRule, SupervisionRuleFormData, SupervisionRulesApiResponse, InsuranceType } from '../types/supervisionRules';

export const supervisionRulesApi = {
  // Get all supervision rules
  async getAll(params?: {
    includeInactive?: boolean;
    insuranceType?: InsuranceType;
  }): Promise<SupervisionRulesApiResponse> {
    const searchParams = new URLSearchParams();
    
    if (params?.includeInactive) {
      searchParams.append('includeInactive', 'true');
    }
    
    if (params?.insuranceType) {
      searchParams.append('insuranceType', params.insuranceType);
    }
    
    const queryString = searchParams.toString();
    const url = `/supervision-rules${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response.data;
  },

  // Get supervision rule by ID
  async getById(id: string): Promise<SupervisionFrequencyRule> {
    const response = await api.get(`/supervision-rules/${id}`);
    return response.data;
  },

  // Create new supervision rule
  async create(data: SupervisionRuleFormData): Promise<SupervisionFrequencyRule> {
    const response = await api.post('/supervision-rules', data);
    return response.data;
  },

  // Update supervision rule
  async update(id: string, data: Partial<SupervisionRuleFormData>): Promise<SupervisionFrequencyRule> {
    const response = await api.put(`/supervision-rules/${id}`, data);
    return response.data;
  },

  // Delete (deactivate) supervision rule
  async delete(id: string): Promise<{ message: string; rule: SupervisionFrequencyRule }> {
    const response = await api.delete(`/supervision-rules/${id}`);
    return response.data;
  },

  // Reactivate supervision rule
  async activate(id: string): Promise<{ message: string; rule: SupervisionFrequencyRule }> {
    const response = await api.post(`/supervision-rules/${id}/activate`);
    return response.data;
  },

  // Bulk operations
  async getHistory(params?: {
    ruleId?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const searchParams = new URLSearchParams();
    
    if (params?.ruleId) {
      searchParams.append('ruleId', params.ruleId);
    }
    
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    
    if (params?.offset) {
      searchParams.append('offset', params.offset.toString());
    }
    
    const queryString = searchParams.toString();
    const url = `/supervision-rules/bulk/history${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response.data;
  },

  async exportToCsv(params?: {
    includeInactive?: boolean;
  }): Promise<Blob> {
    const searchParams = new URLSearchParams();
    
    if (params?.includeInactive) {
      searchParams.append('includeInactive', 'true');
    }
    
    const queryString = searchParams.toString();
    const url = `/supervision-rules/bulk/export${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url, {
      responseType: 'blob'
    });
    return response.data;
  },

  async importFromCsv(data: {
    csvData: string;
    validateOnly?: boolean;
  }): Promise<any> {
    const response = await api.post('/supervision-rules/bulk/import', data);
    return response.data;
  },

  async bulkUpdate(data: {
    rules: Array<{
      id: string;
      [key: string]: any;
    }>;
  }): Promise<any> {
    const response = await api.post('/supervision-rules/bulk/update', data);
    return response.data;
  }
};
